import React, {
	useState,
	useRef,
	useEffect,
	useCallback,
} from 'react';

import style from './ListingConfig.module.css';

function AIPrompt(props) {
	const {
		classNameList,
		label,
		value,
		defaultValue,
		setValue = () => {},
		setPrompt = () => {},
		dataTestingInfo,
		isDisabled,
		availableFields = [],
		listingColumnUUID,
	} = props;

	const [ inputValue, setInputValue, ] = useState(defaultValue || value || '');
	const [ isEditing, setIsEditing, ] = useState(false);
	const [ showSuggestions, setShowSuggestions, ] = useState(false);
	const [ suggestions, setSuggestions, ] = useState([]);
	const [ selectedSuggestionIndex, setSelectedSuggestionIndex, ] = useState(0);
	const [ suggestionTriggerPosition, setSuggestionTriggerPosition, ] = useState(null);
	const textareaRef = useRef(null);
	const suggestionsRef = useRef(null);
	const setPromptTimeoutRef = useRef(null);

	useEffect(() => {
		if (defaultValue !== undefined) {
			setInputValue(defaultValue);
		} else if (value !== undefined) {
			setInputValue(value);
		}
	}, [ defaultValue, value, ]);

	// Move cursor to end when entering edit mode
	useEffect(() => {
		if (isEditing && textareaRef.current) {
			const length = textareaRef.current.value.length;
			textareaRef.current.selectionStart = length;
			textareaRef.current.selectionEnd = length;
		}
	}, [ isEditing, ]);

	// Check for {{ pattern and show suggestions
	const checkForSuggestionTrigger = useCallback((text, cursorPosition) => {
		const textBeforeCursor = text.substring(0, cursorPosition);
		const lastOpenBraces = textBeforeCursor.lastIndexOf('{{');

		if (lastOpenBraces === -1) {
			setShowSuggestions(false);
			return;
		}

		// Check if there's a closing }} between the {{ and cursor
		const textAfterBraces = textBeforeCursor.substring(lastOpenBraces + 2);
		if (textAfterBraces.includes('}}')) {
			setShowSuggestions(false);
			return;
		}

		// Get the search term (text after {{)
		const searchTerm = textAfterBraces.toLowerCase();

		// Filter available fields based on search term
		const filteredSuggestions = availableFields.filter((field) => {
			return field.label.toLowerCase().includes(searchTerm);
		});

		if (filteredSuggestions.length > 0) {
			setSuggestions(filteredSuggestions);
			setSuggestionTriggerPosition(lastOpenBraces);
			setShowSuggestions(true);
			setSelectedSuggestionIndex(0);
		} else {
			setShowSuggestions(false);
		}
	}, [ availableFields, ]);

	// Insert the selected suggestion
	const insertSuggestion = useCallback((suggestion) => {
		if (suggestionTriggerPosition === null || !textareaRef.current) return;

		const cursorPosition = textareaRef.current.selectionStart;
		const textBeforeTrigger = inputValue.substring(0, suggestionTriggerPosition);
		let textAfterCursor = inputValue.substring(cursorPosition);
		textAfterCursor = textAfterCursor.replace(/^\}{1,2}/, '');

		const newValue = `${textBeforeTrigger}{{${suggestion.label}}}${textAfterCursor}`;
		setInputValue(newValue);
		if (setValue) {
			setValue(newValue);
		}
		setShowSuggestions(false);

		// Set cursor position after the inserted text
		setTimeout(() => {
			if (textareaRef.current) {
				const newCursorPosition = textBeforeTrigger.length + suggestion.label.length + 4; // +4 for {{}}
				textareaRef.current.selectionStart = newCursorPosition;
				textareaRef.current.selectionEnd = newCursorPosition;
				textareaRef.current.focus();
			}
		}, 0);
	}, [ inputValue, setValue, suggestionTriggerPosition, ]);

	// Handle keyboard navigation
	const handleKeyDown = (e) => {
		if (!showSuggestions) return;

		switch (e.key) {
		case 'ArrowDown':
			e.preventDefault();
			setSelectedSuggestionIndex((prev) => {
				return prev < suggestions.length - 1 ? prev + 1 : prev;
			});
			break;
		case 'ArrowUp':
			e.preventDefault();
			setSelectedSuggestionIndex((prev) => {
				return prev > 0 ? prev - 1 : prev;
			});
			break;
		case 'Enter':
		case 'Tab':
			if (suggestions[selectedSuggestionIndex]) {
				e.preventDefault();
				insertSuggestion(suggestions[selectedSuggestionIndex]);
			}
			break;
		case 'Escape':
			setShowSuggestions(false);
			break;
		default:
			break;
		}
	};

	const throttledSetPrompt = useCallback((prompt, timeout = 0) => {
		clearTimeout(setPromptTimeoutRef.current);
		setPromptTimeoutRef.current = setTimeout(() => {
			setPrompt(listingColumnUUID, prompt);
		}, timeout);
	}, [ listingColumnUUID, setPrompt, ]);

	useEffect(() => {
		return () => {
			clearTimeout(setPromptTimeoutRef.current);
		};
	}, []);

	useEffect(() => {
		throttledSetPrompt(value, 200);
	}, [ throttledSetPrompt, value, ]);

	// Handle input change
	const handleInputChange = (e) => {
		const newValue = e.target.value;
		setInputValue(newValue);
		if (setValue) {
			setValue(newValue);
		}

		// Check for suggestion trigger
		const cursorPosition = e.target.selectionStart;
		checkForSuggestionTrigger(newValue, cursorPosition);
	};

	// Function to render text with highlighted placeholders
	const renderHighlightedText = (text) => {
		if (!text) return null;

		const placeholderPattern = /(\{\{[^}]+\}\})/g;
		const parts = text.split(placeholderPattern);

		return parts.map((part, index) => {
			if (part.match(placeholderPattern)) {
				// Extract the field name from {{fieldName}}
				const fieldName = part.slice(2, -2);
				const isValidField = availableFields.some((field) => {
					return field.label.toLowerCase() === fieldName.toLowerCase();
				});

				return (
					<span
						key={index}
						className={isValidField ? style.aiPromptPlaceholderHighlight : style.aiPromptPlaceholderInvalid}
					>
						{part}
					</span>
				);
			}
			return <span key={index}>{part}</span>;
		});
	};

	let classNames = `text-small ${(classNameList || []).join(' ')}`;

	return (
		<div className={classNames}>
			{label &&
				<div className="label">
					<label>{label}</label>
				</div>
			}

			{!isEditing && (
				<div
					className={`${style.aiPromptDisplay} ${isDisabled ? style.disabled : ''}`}
					onClick={() => {
						if (isDisabled) {
							return;
						}
						setIsEditing(true);
					}}
					data-testing-info={dataTestingInfo ? `${dataTestingInfo}-display` : ''}
				>
					{renderHighlightedText(inputValue) || <span className={style.aiPromptPlaceholder}>Click to edit...</span>}
				</div>
			)}

			{isEditing && (
				<div className={style.aiPromptTextareaContainer}>
					<textarea
						ref={textareaRef}
						className={style.aiPromptTextarea}
						value={inputValue}
						disabled={isDisabled}
						onChange={handleInputChange}
						onKeyDown={handleKeyDown}
						onBlur={(e) => {
							// Delay blur to allow clicking on suggestions
							setTimeout(() => {
								if (!suggestionsRef.current?.contains(document.activeElement)) {
									const trimmedValue = e.target.value.trim();
									setInputValue(trimmedValue);
									setIsEditing(false);
									setShowSuggestions(false);
									if (setValue) {
										setValue(trimmedValue);
									}
									throttledSetPrompt(trimmedValue, 0);
								}
							}, 150);
						}}
						autoFocus
						data-testing-info={dataTestingInfo ? dataTestingInfo : ''}
					/>
					{showSuggestions && suggestions.length > 0 && (
						<div ref={suggestionsRef} className={style.aiPromptSuggestions}>
							<div className={style.aiPromptSuggestionsHeader}>
								Available Fields
							</div>
							{suggestions.map((suggestion, index) => {
								return <div
									key={suggestion.value}
									className={`${style.aiPromptSuggestionItem} ${index === selectedSuggestionIndex ? style.aiPromptSuggestionItemSelected : ''}`}
									onMouseDown={(e) => {
										e.preventDefault();
										insertSuggestion(suggestion);
									}}
									onMouseEnter={() => {
										return setSelectedSuggestionIndex(index);
									}}
								>
									{suggestion.label}
								</div>;
							})}
						</div>
					)}
				</div>
			)}
		</div>
	);
}

export default AIPrompt;